import { z } from "zod";

import Timeline, {
  type TimelineProps,
} from "@/components/ui/chat/timeline/Timeline";

/** Chat catalog definition for Timeline. Use in renderer catalog and explorer catalog. */
export const ChatTimelineSpec = {
  props: z.object({
    /** Array of timeline events to display in chronological order. */
    items: z.array(
      z.object({
        /** Event title (required). */
        title: z.string(),
        /** Optional longer description of the event. */
        description: z.string().nullable().optional(),
        /** Optional date or time label (e.g. "June 2025", "Q3 2024"). */
        date: z.string().nullable().optional(),
        /**
         * Visual status of this event on the timeline.
         * `completed` = filled primary dot, `current` = accent dot,
         * `upcoming` = muted dot. Defaults to `completed` when omitted.
         */
        status: z
          .enum(["completed", "current", "upcoming"])
          .nullable()
          .optional(),
      }),
    ),
    /**
     * Optional heading rendered above the timeline.
     * Use for context like "Project milestones" or "Coverage timeline".
     */
    heading: z.string().nullable().optional(),
  }),
  description:
    "Vertical timeline for ordered events, milestones, or chronological sequences. Each item has a title (required), optional description, optional date badge, and optional status ('completed' | 'current' | 'upcoming'). Use for project plans, coverage timelines, historical summaries, or step-by-step progressions. Prefer Timeline over numbered lists when temporal order or status matters.",
  example: {
    heading: "Coverage timeline",
    items: [
      {
        title: "Initial press release",
        description: "Reuters and AP picked up the announcement within hours.",
        date: "Jan 15, 2025",
        status: "completed",
      },
      {
        title: "Analyst coverage begins",
        description:
          "Major financial outlets published in-depth analysis pieces.",
        date: "Jan 22, 2025",
        status: "current",
      },
      {
        title: "Earnings call",
        date: "Feb 1, 2025",
        status: "upcoming",
      },
    ],
  },
} as const;

type TimelinePropsFromSpec = {
  items: Array<{
    title: string;
    description?: string | null;
    date?: string | null;
    status?: "completed" | "current" | "upcoming" | null;
  }>;
  heading?: string | null;
};

/**
 * Adapter for json-render defineRegistry: renders Timeline from catalog props.
 */
export const ChatTimelineFromProps = ({
  props,
}: {
  props: Record<string, unknown>;
}) => {
  const { items, heading } = props as TimelinePropsFromSpec;
  return (
    <div className="w-full min-w-0">
      <Timeline
        items={items as TimelineProps["items"]}
        heading={heading ?? undefined}
      />
    </div>
  );
};
